import { resolvedEventBookingType, TDR } from 'tdr-common';
import { SearchFilters } from '../routes/AvailabilityGuard';
import { mostCompatibleGroupSize } from './groupSIze';
import { resolveEventBookNowGroupSize } from './resolveEventBookNowGroupSize';

export function eventSpecifiesGuestCount(event: TDR.Event): boolean {
	const v = event.condition?.value;
	return typeof v?.minGuestCount === 'number' || typeof v?.maxGuestCount === 'number';
}

export function eventSpecifiesTime(event: TDR.Event): boolean {
	const between = event.condition?.value?.betweenTimes;
	return Array.isArray(between) && between.length > 0 && String(between[0] ?? '').trim() !== '';
}

/** True when the event constrains which calendar dates apply (not permanent / open-ended). */
export function eventSpecifiesDateSchedule(event: TDR.Event): boolean {
	const sched = event.eventSchedule ?? 'non-recurring';
	if (sched === 'permanent') {
		return false;
	}
	if (sched === 'recurring') {
		return true;
	}
	const v = event.condition?.value;
	return !!(v?.dates?.length || v?.betweenDates?.length === 2);
}

/** Defaults aligned with results-page URL initialization (group size from tables; date/time from URL). */
export function getResultsPageDefaultSearchFilters(
	tables: TDR.Table[],
	currentFilters: SearchFilters
): Pick<SearchFilters, 'groupSize'> & Partial<Pick<SearchFilters, 'date' | 'time'>> {
	const raw = mostCompatibleGroupSize(tables);
	const computedGroupSize = raw === -1 ? 2 : raw;
	return {
		groupSize: currentFilters.groupSize ?? computedGroupSize,
		date: currentFilters.date,
		time: currentFilters.time
	};
}

/**
 * First search filters that satisfy the event's configured constraints.
 *
 * The results page can never carry a partial set of availability params (see
 * useTestOpUrlNormalization, which strips groupSize/date/time back to nothing unless
 * all three are set), so selecting an event must always resolve all three together —
 * firstDate/firstTime come from a scan that already honors any date/time constraints
 * the event defines, or just finds the next open slot when it doesn't.
 */
export function buildEventResultsSearchFilters(
	event: TDR.Event,
	tables: TDR.Table[],
	currentFilters: SearchFilters,
	firstDate: string | null,
	firstTime: string | null
): Partial<SearchFilters> | null {
	if (resolvedEventBookingType(event) !== 'param') {
		return null;
	}

	const defaults = getResultsPageDefaultSearchFilters(tables, currentFilters);

	const groupSize = eventSpecifiesGuestCount(event)
		? resolveEventBookNowGroupSize(event, tables)
		: defaults.groupSize;

	if (!groupSize || !firstDate || !firstTime) {
		return null;
	}

	return { groupSize, date: firstDate, time: firstTime };
}
